import {
  Card,
  Form,
  Button,
  Input,
  Col,
  Row,
  Switch,
  Collapse,
  message,
  Tooltip,
  Space,
} from 'antd';
import {
  useCompanyData,
  useEventNames,
  useSendTelegramTestMessage,
  useUpdateCompanyAlert,
} from '../../../../../../../../Hooks/TT_ELD/Companies';
import { InfoCircleOutlined } from '@ant-design/icons';

type MyObjectType = {
  [key: string | number]: any;
};

type EventNamesData = {
  [key: string]: {
    title: string;
  };
};

const { Panel } = Collapse;

const groups = {
  'Harsh Driving': ['rapid-acc', 'rapid-dec', 'harsh-turn'],
  Crash: ['collision'],
  'Distracted Driving': [
    'distracted-driving',
    'phone-calls',
    'fatigue-driving',
    'overtime-fatigue-driving',
  ],
  'Policy Violations': ['no-seatbelt'],
  Speeding: ['overspeed'],
  Other: ['sos-emergency', 'low-vehicle-battery'],
};

const formatGroupedOptions = (data: EventNamesData = {}) => {
  const grouped: { label: string; options: { value: string; label: string }[] }[] = [];

  const groupedKnown = Object.entries(groups).map(([groupName, keys]) => ({
    label: groupName,
    options: keys
      .filter((key) => key in data)
      .map((key) => ({
        value: key,
        label: data[key].title,
      })),
  }));

  grouped.push(...groupedKnown);

  const knownKeys = Object.values(groups).flat();
  const unknownKeys = Object.keys(data).filter((key) => !knownKeys.includes(key));

  if (unknownKeys.length > 0) {
    grouped.push({
      label: 'Other',
      options: unknownKeys.map((key) => ({
        value: key,
        label: data[key].title,
      })),
    });
  }

  return grouped;
};

const normalize = (val: any) => {
  if (val === '' || val === undefined || val === null) return null;
  return val;
};

const Alerts = ({ companyId }: { companyId: any }) => {
  const [form] = Form.useForm();

  const { data, isFetching, isLoading, refetch }: MyObjectType = useCompanyData(companyId);
  const { data: eventNamesData } = useEventNames();
  const { mutate: updateCompanyAlert, isLoading: isPending } = useUpdateCompanyAlert();

  const { mutate: sendTestMessage, isLoading: isTestPending } = useSendTelegramTestMessage();

  const groupedOptions = formatGroupedOptions(eventNamesData);

  const initialSettings = data?.settings
    ? Object.fromEntries(Object.entries(data.settings).map(([key, value]) => [key, !!value]))
    : {};

  const onSubmit = (values: any) => {
    const newSettings: Record<string, boolean> = {};

    Object.keys(eventNamesData || {}).forEach((key) => {
      newSettings[key] = !!values[key];
    });

    const oldSettings = data?.settings || {};
    const changedSettings: Record<string, boolean> = {};

    Object.keys(newSettings).forEach((key) => {
      if (oldSettings[key] !== newSettings[key]) {
        changedSettings[key] = newSettings[key];
      }
    });

    const newChatId = normalize(values.cameraAlertChatId);
    const newEmail = normalize(values.cameraAlertEmail);

    const body: any = {};

    if (data.cameraAlertChatId !== newChatId) {
      body.cameraAlertChatId = newChatId;
    }
    if (data.cameraAlertEmail !== newEmail) {
      body.cameraAlertEmail = newEmail;
    }

    if (Object.keys(changedSettings).length > 0) {
      body.settings = changedSettings;
    }

    if (Object.keys(body).length === 0) {
      message.warning('No changes detected');
      return;
    }

    updateCompanyAlert({
      companyId,
      body,
    });

    refetch();
  };

  const handleCameraAlertTestMessage = () => {
    const chatId = form.getFieldValue('cameraAlertChatId');

    if (!chatId) return;

    sendTestMessage({
      chatId,
      botType: 'protrux',
      message: 'Success! Your chat has been connected, and notifications will now be sent here',
    });
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <Card loading={isLoading || isFetching} style={{ width: '100%' }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        onFinishFailed={onFinishFailed}
        name="alerts-form"
        initialValues={{
          ...data,
          ...initialSettings,
          cameraAlertChatId: data?.cameraAlertChatId,
          cameraAlertEmail: data?.cameraAlertEmail,
        }}
      >
        <Row gutter={[16, 10]}>
          <Col xs={24} sm={24} md={12} lg={8}>
            <Form.Item
              label={
                <span>
                  Camera Alert Chat ID{' '}
                  <span style={{ color: '#999' }}>(Supported bots: @protrux_dashcam_bot)</span>
                </span>
              }
            >
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item noStyle name="cameraAlertChatId">
                  <Input placeholder="Enter Chat ID" />
                </Form.Item>
                <Button
                  type="primary"
                  loading={isTestPending}
                  onClick={handleCameraAlertTestMessage}
                >
                  Test Message
                </Button>
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={8}>
            <Form.Item label="Notification Email" name="cameraAlertEmail">
              <Input placeholder="Enter Email" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={12} lg={8}>
            <Collapse defaultActiveKey={[groupedOptions[0]?.label]}>
              {groupedOptions.map((group) => (
                <Panel header={group.label} key={group.label}>
                  {group.options.map((item) => (
                    <Form.Item
                      key={item.value}
                      name={item.value}
                      label={item.label}
                      valuePropName="checked"
                      style={{ marginBottom: 8 }}
                    >
                      <Switch />
                    </Form.Item>
                  ))}
                </Panel>
              ))}
            </Collapse>
          </Col>
        </Row>

        <Form.Item style={{ marginTop: 20 }}>
          <Button type="primary" htmlType="submit" loading={isPending}>
            Submit
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default Alerts;
